import { useCallback, useEffect, useState } from 'react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Scatter,
  ReferenceLine,
} from 'recharts';
import CustomTooltip from './components/customTooltip/CustomTooltip';
import './chart.css';
import { useNavigate } from 'react-router-dom';
import {
  CFR_CHART_KEYS,
  CONTRIBUTOR_CHART_KEYS,
  CUSTOM_CHART_KEYS,
  DEPENDENCY_VULNERABILITIES_CHART_KEYS,
  DEPLOY_CHART_KEYS,
  DOCUMENT_UPDATED_CHARTS,
  GOAL_METRIC_GIT_KEYS,
  GOAL_METRIC_JIRA_KEYS,
  ISSUE_CHART_KEYS,
  ISSUE_LIFECYCLE_DIST_CHARTS,
  MEETING_CHARTS,
  PR_CHART_KEYS,
  PR_HEALTH_INDEX_CHARTS,
  QUALITY_CHART_KEYS,
  REQUIREMENT_STABILITY_CHARTS,
  splitTime,
  SPRINT_CHARTS,
  TEST_DETAIL_CHARTS,
  TEST_RUN_CHARTS,
} from '../shared/utils';
import MetricSidebar from './components/MetricSidebar';
import moment from 'moment';
import { WELLBEING_CHART_KEYS } from '../shared/utils/index';
import { useTheme, Box } from '@chakra-ui/react';
import CustomLegend from '../customLegend';
import { applyWarpToPercentiles, unwarpY, warpY } from './utils/charts.utils';
import { DateRangeSelection } from '../shared/typings';

export interface ControlChartDataItem {
  endDate: string;
  xLabelName: string;
  x: string;
  y: number;
  startDate: string;
  yPercentile: number;
  clusterP0ToP5Percentile?: number;
  clusterP0ToP5?: number;
  clusterP90ToP95?: number;
  clusterP95ToP100?: number;
  clusterP5ToP10?: number;
  clusterP90ToP95Percentile?: number;
  clusterP95ToP100Percentile?: number;
  clusterP5ToP10Percentile?: number;
  xLabelId?: string;
  yRollingAvg?: number;
  [key: string]: string | number | undefined;
}

export interface ControlChartKey {
  key: string;
  name: string;
  color: string;
  chartType: string;
}

export interface ControlChartRefLine {
  percentile: number;
  name: string;
  color: string;
  value: string;
  lineType: 'plain' | 'dotted';
}

interface ControlChartProps {
  chartref?: any;
  data: ControlChartDataItem[];
  keys?: ControlChartKey[];
  chartMetadata?: any;
  showLabel?: boolean;
  selectedTeam: string;
  selected?: DateRangeSelection;
  hideBtn?: boolean;
  slant?: boolean;
  granularSlant?: boolean;
  ticketType?: string;
  forDetails?: boolean;
  queryKey?: string;
  repos?: string[];
  showYLabel?: boolean;
  showXLabel?: boolean;
  showCustomLegend?: boolean;
  detailView?: boolean;
  refLines?: ControlChartRefLine[];
}

//This component is used to show the outlier detection for PR Cycle Time, PR Coding Time, PR Pickup Time and Merge Cycle Time
export const ControlChart = ({
  chartref,
  data,
  keys,
  chartMetadata,
  showLabel,
  selectedTeam,
  selected,
  hideBtn,
  slant,
  granularSlant,
  ticketType,
  forDetails,
  queryKey,
  repos,
  showYLabel,
  showCustomLegend,
  showXLabel,
  detailView,
  refLines,
}: ControlChartProps) => {
  const { colors } = useTheme();
  const [hoveredLabel, setHoveredLabel] = useState<any>(null);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [clickedData, setClickedData] = useState<any>(null);
  const [currentChartMeta, setCurrentChartMeta] = useState<any>(null);
  const [barProps, setBarProps] = useState<any>({});

  useEffect(() => {
    setBarProps(
      keys?.reduce(
        (a: any, { key }: ControlChartKey) => {
          a[key] = true;
          return a;
        },
        {
          hover: null,
        }
      )
    );
  }, [keys]);

  const params = new URLSearchParams(window.location.search);
  const team = params.get('team');

  const handleModalClose = useCallback(() => {
    setIsOpen(false);
  }, [isOpen]);

  const handleModalOpen = useCallback(() => {
    setIsOpen(true);
  }, [isOpen]);

  const handleClick = (label: { value: string }) => {
    const foundItem = data?.find((el) => el.xLabelName === label.value);
    if (foundItem?.xLabelId) {
      navigate(`/sprint/${foundItem.xLabelId}${team ? `?team=${team}` : ''}`);
    }
  };

  const handleMouseEnter = (label: any) => {
    setHoveredLabel(label);
  };

  const handleMouseLeave = () => {
    setHoveredLabel(null);
  };

  const renderCustomTick = (props: any) => {
    const { x, y, payload } = props;
    const { value } = payload;
    const isHovered = value === hoveredLabel;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={4}
          fill={colors.primary}
          fontSize={7.5}
          fontWeight={isHovered ? 700 : 600}
          cursor="pointer"
          textAnchor="end"
          textDecoration={'underline'}
          onMouseEnter={() => handleMouseEnter(value)}
          onMouseLeave={handleMouseLeave}
          transform="rotate(-20)"
        >
          {value}
        </text>
      </g>
    );
  };

  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const [hideTooltip, setHideTooltip] = useState(false);

  const handleTooltipHoverChange = (hovered: boolean) => {
    setIsTooltipHovered(hovered);
  };

  return (
    <Box style={{ position: 'relative', width: '100%', height: '100%' }}>
      <ResponsiveContainer ref={chartref} width="100%" height="100%">
        <ComposedChart
          data={applyWarpToPercentiles(data)}
          margin={{
            top: 10,
            left: showLabel || showYLabel ? 0 : slant ? 108 : -5,
            bottom: granularSlant || slant ? 52 : 0,
          }}
          onMouseEnter={() => {
            handleTooltipHoverChange(false);
            setHideTooltip(false);
          }}
          onMouseLeave={() => {
            handleTooltipHoverChange(true);
            setHideTooltip(true);
          }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            horizontal={false}
          />
          <XAxis
            dataKey={chartMetadata.xAxis}
            axisLine={true}
            tickLine={false}
            padding={{ left: 20, right: 20 }}
            style={{
              fontSize: 7.5,
              fontWeight: 500,
              cursor: slant ? 'pointer' : undefined,
            }}
            angle={slant || granularSlant ? -60 : undefined}
            textAnchor={granularSlant || slant ? 'end' : 'middle'}
            interval={granularSlant || slant ? 0 : undefined}
            onClick={slant ? handleClick : undefined}
            tick={slant ? renderCustomTick : undefined}
            label={
              showXLabel
                ? {
                    value: chartMetadata.xlabel,
                    fontSize: 11,
                    fontWeight: 400,
                    position: 'insideBottom',
                    dy: detailView ? 40 : 5,
                  }
                : {}
            }
          />

          <YAxis
            domain={[0, 100]}
            tickFormatter={(v) => `${unwarpY(v)?.toFixed(0)}%`}
            ticks={[0, 10, 30, 50, 70, 90, 100]}
            interval={1}
          />

          <Tooltip
            content={
              <CustomTooltip
                handleModalOpen={handleModalOpen}
                setClickedData={setClickedData}
                unit={chartMetadata.ylabel}
                onClickAction={() => {
                  setCurrentChartMeta(chartMetadata);
                }}
                chartKey={chartMetadata?.chartKey}
                hideTooltip={hideTooltip}
                hideBtn={hideBtn}
                onHoverChange={handleTooltipHoverChange}
              />
            }
            trigger={isTooltipHovered ? 'click' : 'hover'}
            wrapperStyle={{ outline: 'none' }}
            position={{ y: -30 }}
            allowEscapeViewBox={{ x: false, y: false }}
          />

          {showLabel && (
            <Legend
              verticalAlign="middle"
              layout="vertical"
              iconType="circle"
              iconSize={11}
              align="right"
              content={
                <CustomLegend
                  keys={keys}
                  additionalLegend={refLines}
                  barProps={barProps}
                  setBarProps={setBarProps}
                  w={200}
                  improvement={chartMetadata?.improvement}
                />
              }
              wrapperStyle={{
                right: -8,
                fontSize: 11.5,
                top: 0,
              }}
            />
          )}

          {refLines?.map((item) => (
            <ReferenceLine
              key={item.percentile}
              y={warpY(item.percentile)}
              stroke={item.color}
              strokeDasharray={item.lineType === 'dotted' ? '3 3' : ''}
              strokeWidth={1}
              opacity={0.5}
              label={({ viewBox }) => {
                const { x, width, y } = viewBox;
                return (
                  <text
                    x={x + width}
                    y={y + 12}
                    textAnchor="end"
                    fill={item.color}
                    fontSize={9}
                  >
                    {splitTime(Number(item.value) / 60)}
                  </text>
                );
              }}
            />
          ))}

          {keys?.map((el) =>
            el.key === 'yRollingAvg' || el.chartType === 'dashed_line_chart' ? (
              <Line
                key={el.key}
                type="monotone"
                dataKey={el.key}
                name={el.name}
                stroke={el.color}
                dot={false}
                activeDot={false}
                strokeWidth={2}
                strokeDasharray="3 6"
                opacity={barProps.hover && barProps.hover !== el.key ? 0.25 : 1}
                hide={barProps[el.key as string] === false}
              />
            ) : el.chartType === 'line_chart' ? (
              <Line
                key={el.key}
                dataKey={el.key}
                name={el.name}
                stroke={el.color}
                strokeWidth={2}
                opacity={barProps.hover && barProps.hover !== el.key ? 0.25 : 1}
                hide={barProps[el.key as string] === false}
                activeDot={
                  [
                    'SPRINT_ADDITION_RATE',
                    'SPRINT_COMPLETION_PERCENT',
                  ].includes(chartMetadata.chartKey)
                    ? { r: 6, strokeWidth: 3, stroke: '#9DB2BF' }
                    : {
                        r: 6,
                        strokeWidth: 3,
                        stroke: '#9DB2BF',
                        cursor: 'pointer',
                        onClick: (event: any, payload: any) => {
                          handleModalOpen();
                          setClickedData({
                            ...payload,
                            name: el.name,
                            id: el.key,
                          });
                          setCurrentChartMeta(chartMetadata);
                        },
                        className: 'active-dot',
                      }
                }
                dot={{
                  fill: el.color,
                  stroke: el.color,
                  strokeWidth: 3,
                  r: 2,
                }}
              />
            ) : el.chartType === 'scatter_chart' ? (
              <Scatter
                key={el.key}
                dataKey={el.key}
                name={el.name}
                fill={el.color}
                onClick={(payload: any) => {
                  handleModalOpen();
                  setClickedData({ ...payload, name: el.name, id: el.key });
                  setCurrentChartMeta(chartMetadata);
                }}
                hide={barProps[el.key as string] === false}
                shape={(props: any) => {
                  return props.cy ? (
                    <circle
                      cx={props.cx}
                      cy={props.cy}
                      r={7}
                      fill={props.fill}
                      opacity={0.6}
                      style={{ cursor: 'pointer' }}
                      stroke="#626266"
                      strokeWidth={1}
                    />
                  ) : (
                    <g />
                  );
                }}
              />
            ) : (
              <Line
                key={el.key}
                dataKey={el.key}
                name={el.name}
                stroke={el.color}
                strokeWidth={2}
                opacity={barProps.hover && barProps.hover !== el.key ? 0.25 : 1}
                hide={barProps[el.key as string] === false}
                activeDot={
                  [
                    'SPRINT_ADDITION_RATE',
                    'SPRINT_COMPLETION_PERCENT',
                  ].includes(chartMetadata.chartKey)
                    ? { r: 6, strokeWidth: 3, stroke: '#9DB2BF' }
                    : {
                        r: 6,
                        strokeWidth: 3,
                        stroke: '#9DB2BF',
                        cursor: 'pointer',
                        onClick: (event: any, payload: any) => {
                          handleModalOpen();
                          setClickedData({
                            ...payload,
                            name: el.name,
                            id: el.key,
                          });
                          setCurrentChartMeta(chartMetadata);
                        },
                        className: 'active-dot',
                      }
                }
                dot={{
                  fill: el.color,
                  stroke: el.color,
                  strokeWidth: 3,
                  r: 2,
                }}
              />
            )
          )}
        </ComposedChart>
      </ResponsiveContainer>
      {[
        ...PR_CHART_KEYS,
        ...CFR_CHART_KEYS,
        ...DEPLOY_CHART_KEYS,
        ...CONTRIBUTOR_CHART_KEYS,
        ...ISSUE_CHART_KEYS,
        ...GOAL_METRIC_GIT_KEYS,
        ...GOAL_METRIC_JIRA_KEYS,
        ...WELLBEING_CHART_KEYS,
        ...QUALITY_CHART_KEYS,
        ...CUSTOM_CHART_KEYS,
        ...REQUIREMENT_STABILITY_CHARTS,
        ...DEPENDENCY_VULNERABILITIES_CHART_KEYS,
        ...MEETING_CHARTS,
        ...PR_HEALTH_INDEX_CHARTS,
        ...DOCUMENT_UPDATED_CHARTS,
        ...SPRINT_CHARTS,
        ...ISSUE_LIFECYCLE_DIST_CHARTS,
        ...TEST_RUN_CHARTS,
        ...TEST_DETAIL_CHARTS,
      ].includes(currentChartMeta?.chartKey) &&
        isOpen && (
          <MetricSidebar
            hideMoreDetails={true}
            querykey={queryKey}
            openDrawer={isOpen}
            handleDrawerClose={handleModalClose}
            heading={chartMetadata.chartTitle}
            subHeading={
              currentChartMeta?.chartKey === REQUIREMENT_STABILITY_CHARTS[0]
                ? `${moment(clickedData[0]?.payload?.xLabelName).format(
                    'DD MMM'
                  )}`
                : `${
                    Array.isArray(clickedData)
                      ? moment(clickedData[0]?.payload?.startDate).format(
                          'DD MMM'
                        )
                      : moment(clickedData?.payload?.startDate).format('DD MMM')
                  } - ${
                    Array.isArray(clickedData)
                      ? moment(clickedData[0]?.payload?.endDate).format(
                          'DD MMM'
                        )
                      : moment(clickedData?.payload?.endDate).format('DD MMM')
                  }${
                    QUALITY_CHART_KEYS?.includes(currentChartMeta?.chartKey)
                      ? `, ${clickedData?.name} ${
                          clickedData?.value ? `(${clickedData?.value})` : ''
                        }`
                      : clickedData || (clickedData && clickedData[0])
                      ? `${
                          clickedData[0]?.name === 'Hours' ||
                          clickedData?.name === 'Hours'
                            ? ''
                            : `, ${clickedData[0]?.name || clickedData?.name}${
                                clickedData[0]?.payload?.y ||
                                clickedData?.payload?.y
                                  ? ['TEST_RUN_TIME'].includes(
                                      currentChartMeta?.chartKey
                                    )
                                    ? ` (${
                                        (clickedData[0]?.payload?.y &&
                                          splitTime(
                                            clickedData[0]?.payload?.y
                                          )) ||
                                        (clickedData?.payload?.y &&
                                          splitTime(clickedData?.payload?.y))
                                      })`
                                    : ` (${
                                        clickedData[0]?.payload?.y
                                          ? splitTime(
                                              clickedData[0]?.payload?.y
                                            )
                                          : clickedData?.id ===
                                            'clusterP90ToP95Percentile'
                                          ? splitTime(
                                              clickedData?.payload
                                                ?.clusterP90ToP95
                                            )
                                          : clickedData?.id ===
                                            'clusterP95ToP100Percentile'
                                          ? splitTime(
                                              clickedData?.payload
                                                ?.clusterP95ToP100
                                            )
                                          : clickedData?.id ===
                                            'clusterP5ToP10Percentile'
                                          ? splitTime(
                                              clickedData?.payload
                                                ?.clusterP5ToP10
                                            )
                                          : clickedData?.id ===
                                            'clusterP0ToP5Percentile'
                                          ? splitTime(
                                              clickedData?.payload
                                                ?.clusterP0ToP5
                                            )
                                          : splitTime(clickedData?.payload.y)
                                      })`
                                  : ''
                              }`
                        } `
                      : ''
                  }`
            }
            payload={clickedData}
            currentChartMeta={currentChartMeta}
            filterBy={{
              teamId: selectedTeam,
              type:
                ticketType || chartMetadata.chartKey === 'BUG_THROUGHPUT'
                  ? 'bug'
                  : chartMetadata.chartKey === 'PR_HEALTH_INDEXES'
                  ? 'merged_without_review'
                  : '',
              projectsFilter: repos,
              sprintId: selected?.sprintId,
              showControlChart: true,
              controlChartFilters: {
                p5:
                  clickedData.id === 'clusterP0ToP5Percentile' ||
                  clickedData.id === 'clusterP5ToP10Percentile'
                    ? (() => {
                        const ref = refLines?.find(
                          (ref) => ref.name === '5 Percentile'
                        );
                        return ref?.value ? Number(ref.value) : null;
                      })()
                    : null,
                p10:
                  clickedData.id === 'clusterP5ToP10Percentile' ||
                  !clickedData?.id?.includes('cluster')
                    ? (() => {
                        const ref = refLines?.find(
                          (ref) => ref.name === '10 Percentile'
                        );
                        return ref?.value ? Number(ref.value) : null;
                      })()
                    : null,
                p90:
                  clickedData.id === 'clusterP90ToP95Percentile' ||
                  !clickedData?.id?.includes('cluster')
                    ? (() => {
                        const ref = refLines?.find(
                          (ref) => ref.name === '90 Percentile'
                        );
                        return ref?.value ? Number(ref.value) : null;
                      })()
                    : null,
                p95:
                  clickedData.id === 'clusterP95ToP100Percentile' ||
                  clickedData.id === 'clusterP90ToP95Percentile'
                    ? (() => {
                        const ref = refLines?.find(
                          (ref) => ref.name === '95 Percentile'
                        );
                        return ref?.value ? Number(ref.value) : null;
                      })()
                    : null,
                includeBelowP5:
                  clickedData.id === 'clusterP0ToP5Percentile' ? true : null,
                includeP5ToP10:
                  clickedData.id === 'clusterP5ToP10Percentile' ? true : null,
                includeP10ToP90: !clickedData?.id?.includes('cluster')
                  ? true
                  : null,
                includeP90ToP95:
                  clickedData.id === 'clusterP90ToP95Percentile' ? true : null,
                includeAboveP95:
                  clickedData.id === 'clusterP95ToP100Percentile' ? true : null,
              },
            }}
            repoTitle={repos && repos?.length >= 1}
            selectedTeam={selectedTeam}
          />
        )}
    </Box>
  );
};
